import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  AlertTriangle,
  Cloud,
  Droplets,
  Thermometer,
  Wind,
} from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";
import InteractiveMap from "./InteractiveMap";
import { useCourseProgress } from "../contexts/CourseProgressContext";

const storySpotlights = [
  {
    id: 1,
    title: "Pakistan's Monster Monsoon",
    description:
      "One-third of Pakistan submerged, 33 million people displaced, 1,700 deaths. Economic losses exceeded $40 billion, destroying 2.3 million homes.",
    impact:
      "500% more rainfall than average - a 1-in-100-year event now 50% more likely due to climate change.",
    science:
      "Warming temperatures in the Arabian Sea led to unprecedented water vapor, fueling extreme rainfall while accelerated glacial melt amplified flooding.",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/2022_Pakistan_Floods_-_August_27%2C_2021_vs._August_27%2C_2022_in_Sindh.jpg/960px-2022_Pakistan_Floods_-_August_27%2C_2021_vs._August_27%2C_2022_in_Sindh.jpg",
    region: "South Asia",
    learnMoreUrl: "https://en.wikipedia.org/wiki/2022_Pakistan_floods",
  },
  {
    id: 2,
    title: "UAE 2024 Floods",
    description:
      "Record-breaking floods across UAE emirates after heaviest 24-hour rainfall in 75 years. Khatm Al Shakla received 254.8 mm in under 24 hours, overwhelming drainage systems in both urban and rural areas.",
    impact:
      "Five lives lost, US$2.9-3.4 billion in insured losses. Dubai Airport faced 1,244 flight cancellations, and transportation systems halted across multiple emirates.",
    science:
      "Extreme rainfall linked to a dynamic trough merging with the Sudan low, intensified by anomalously warm Arabian Sea. Climate-driven warming increased atmospheric moisture capacity and slowed jet streams.",
    image:
      "https://media.cnn.com/api/v1/images/stellar/prod/2024-04-17t090228z-1843395528-rc2887ai0vgm-rtrmadp-3-emirates-weather-2.jpg?q=w_1110,c_fill",
    region: "Middle East",
    learnMoreUrl:
      "https://en.wikipedia.org/wiki/2024_United_Arab_Emirates_floods",
  },
  {
    id: 3,
    title: "Greek-Turkish Wildfires",
    description:
      "Largest wildfire in EU history burned 81,000 hectares in Greece. Rhodes evacuation became largest in Greek history, with 20,000 people fleeing.",
    impact:
      "Mediterranean fire seasons typically lasted 2-3 months. Now they span 5-6 months with unprecedented intensity.",
    science:
      "The Mediterranean is warming 20% faster than the global average, creating perfect conditions for 'mega-fires' that create their own weather systems.",
    image:
      "https://static01.nyt.com/images/2023/08/22/multimedia/22greece-fire-01-kzbf/22greece-fire-01-kzbf-superJumbo.jpg",
    region: "Mediterranean",
    learnMoreUrl: "https://en.wikipedia.org/wiki/2023_Greece_wildfires",
  },
  {
    id: 4,
    title: "Hurricane Helene",
    description:
      "Over 238 fatalities with damages exceeding $124 billion. Record-breaking storm surge of 15 feet, with widespread devastation across multiple states.",
    impact:
      "Wind speeds reached 140 mph, potentially exceeding the upper limit of traditional hurricane scales.",
    science:
      "Warmer ocean temperatures fueled by climate change significantly contributed to Helene's rapid intensification and extreme intensity.",
    image:
      "https://oceanservice.noaa.gov/news/sep24/helene-asheville-oct-5-960.jpg",
    region: "North America",
    learnMoreUrl: "https://en.wikipedia.org/wiki/Hurricane_Helene",
  },
];

const personalStories = [
  {
    id: 1,
    name: "Ahmed",
    location: "Bangladesh",
    story:
      "Lost his family home to severe flooding after three generations had lived there. Now forced to relocate to overcrowded urban areas with limited job opportunities.",
    quote:
      "The waters came faster than ever before. My grandfather always said floods were part of life here, but nothing like this.",
  },
  {
    id: 2,
    name: "Maria",
    location: "California",
    story:
      "Evacuated from her home with only minutes to spare as wildfires engulfed her neighborhood. Has relocated three times in five years due to recurring fire threats.",
    quote:
      "We used to prepare for 'fire season' but now it feels like we're always on alert, always ready to flee. This isn't the California I grew up in.",
  },
  {
    id: 3,
    name: "Yasmin",
    location: "Egypt",
    story:
      "A third-generation farmer who can no longer grow the crops her family has traditionally cultivated due to changing rainfall patterns and extreme heat.",
    quote:
      "My grandmother taught me when to plant by watching the skies. Now those patterns mean nothing. The old wisdom doesn't work anymore.",
  },
];

const regionPredictions = [
  {
    region: "South Asia",
    prediction:
      "By 2050, over 800 million South Asians could face annual deadly heat waves, while flooding could displace 40 million in Bangladesh alone.",
  },
  {
    region: "Southeast Asia",
    prediction:
      "Super typhoons could increase in frequency by 14% and intensity by 7%. 95% of North Jakarta could be submerged, costing over $40 billion.",
  },
  {
    region: "Middle East",
    prediction:
      "By 2050, many Middle Eastern cities could experience uninhabitable heat levels for 4-5 months each year.",
  },
  {
    region: "Mediterranean",
    prediction:
      "By 2050, fire seasons could last year-round in parts of the Mediterranean, threatening ancient olive groves and wine regions.",
  },
  {
    region: "Northern Europe",
    prediction:
      "By 2050, extreme floods could become 14 times more likely, with traditional flood defenses proving inadequate.",
  },
  {
    region: "North America",
    prediction:
      "Heat waves that once occurred every 20 years are projected to happen every 2-3 years, with wildfires increasing in frequency and intensity by 30-50%.",
  },
];

const WhyItMatters = () => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const { isModuleCompleted, toggleModuleComplete } = useCourseProgress();

  useEffect(() => {
    // Fade-in the intro text on mount
    setShowIntro(true);

    // Check if module is already completed
    const moduleId = "why-it-matters";
    setIsCompleted(isModuleCompleted(moduleId));
  }, [isModuleCompleted]);

  const handleMarkComplete = () => {
    const newCompletionState = !isCompleted;
    setIsCompleted(newCompletionState);
    toggleModuleComplete("why-it-matters", newCompletionState);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Why It Matters<span className="text-edu-orange">?</span>
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      {/* Animated Intro Section */}
      <section
        className="w-full bg-gray-900 text-white py-12 px-6 text-center flex items-center justify-center mb-12"
        aria-label="Climate change introductory message"
      >
        <h2
          className={`max-w-4xl text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight transition-opacity duration-1000 ${
            showIntro ? "opacity-100" : "opacity-0"
          }`}
        >
          Climate change isn't just melting ice caps or something that{" "}
          <em>"might"</em> happen someday. It's already reshaping lives, homes,
          and economies around the world — maybe even in <u>your</u> city.
        </h2>
      </section>

      {/* Interactive Map Section */}
      <section className="mb-12">
        <div className="bg-primary-100 p-6 rounded-lg mb-6">
          <p className="text-lg text-gray-800">
            Explore climate events around the world. Click on highlighted
            countries to see details about recent extreme weather events and
            future climate predictions.
          </p>
          <div className="mt-4 bg-white rounded-lg p-3 border border-primary-200">
            <div className="flex flex-wrap gap-4 items-center">
              <span className="text-sm font-medium text-gray-700">
                Event types:
              </span>
              <div className="flex items-center">
                <Droplets className="text-blue-500 w-4 h-4 mr-1" />
                <span className="text-sm">Floods</span>
              </div>
              <div className="flex items-center">
                <AlertTriangle className="text-red-500 w-4 h-4 mr-1" />
                <span className="text-sm">Fires</span>
              </div>
              <div className="flex items-center">
                <Wind className="text-purple-500 w-4 h-4 mr-1" />
                <span className="text-sm">Hurricanes</span>
              </div>
              <div className="flex items-center">
                <Thermometer className="text-orange-500 w-4 h-4 mr-1" />
                <span className="text-sm">Heat Waves</span>
              </div>
              <div className="flex items-center">
                <Cloud className="text-yellow-500 w-4 h-4 mr-1" />
                <span className="text-sm">Droughts</span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full rounded-lg shadow-md overflow-hidden mb-6">
          <div className="w-full">
            <InteractiveMap />
          </div>
        </div>
      </section>

      <div className="bg-primary-100 p-6 rounded-lg mb-12">
        <p className="text-lg text-gray-800">
          Climate change isn't just a distant future threat—it's happening now
          and affecting real people around the world. This section explores how
          climate change impacts communities globally, bringing the reality of
          the crisis to life through real stories.
        </p>
      </div>

      {/* Climate Impact Lab Map Section */}
      <section className="mb-16" aria-label="Climate impact projections map">
        <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">
          Explore Climate Impacts Worldwide
        </h2>
        <div className="bg-white rounded-lg shadow-md p-4">
          <p className="text-gray-700 mb-4">
            Explore how climate change will affect different regions through
            2100 with projected temperature changes, economic impacts, and more.
          </p>
          <div className="w-full aspect-[16/9]">
            <iframe
              title="Climate Impact Lab Map"
              src="https://impactlab.org/map/"
              className="w-full h-full border-0 rounded-lg"
            ></iframe>
          </div>
          <p className="text-sm text-center text-gray-600 mt-4">
            <em>
              Data source: Climate Impact Lab (projected climate impacts through
              2100)
            </em>
          </p>
        </div>
      </section>

      {/* Spotlight Stories */}
      <div className="mb-16">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          Climate Crisis Spotlight
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {storySpotlights.map((story) => (
            <div
              key={story.id}
              className="bg-white rounded-xl shadow-md overflow-hidden"
            >
              <img
                src={story.image}
                alt={story.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <div className="text-sm font-medium text-primary-500 mb-1">
                  {story.region}
                </div>
                <h3 className="font-semibold text-lg text-gray-800 mb-2">
                  {story.title}
                </h3>
                <p className="text-gray-600 mb-2">{story.description}</p>
                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Impact:</span>{" "}
                    {story.impact}
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-semibold">Science:</span>{" "}
                    {story.science}
                  </p>
                </div>
                <a
                  href={story.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 text-edu-blue font-medium flex items-center"
                >
                  Learn more
                  <ArrowRight className="ml-1 w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Future Predictions by Region */}
      <section className="mb-16">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          Future Climate Predictions by Region
        </h2>
        <p className="mb-6 text-gray-700">
          Climate scientists have released new projections (IPCC AR6, 2021–2023)
          showing how different regions will likely change in the coming
          decades. Here's what to expect around the world:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {regionPredictions.map((item, index) => (
            <div
              key={index}
              className="bg-white p-5 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
            >
              <h3 className="font-semibold text-primary-600 text-lg mb-2">
                {item.region}
              </h3>
              <p className="text-gray-700">{item.prediction}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Regional Climate Projections in Detail
          </h3>

          <ul className="list-disc list-inside space-y-4 text-gray-700">
            <li>
              <strong>North America:</strong> Temperatures will keep climbing,
              especially in northern areas, with winter warming outpacing the
              global average. More extreme heat and heavy rainfall events are
              expected (virtually certain), while the western and central parts
              face worsening droughts and wildfire risk (high confidence).
              Eastern regions will likely see increasing rainfall and flooding
              incidents.
            </li>
            <li>
              <strong>Europe:</strong> Europe is heating faster than the global
              rate (high confidence). Severe heatwaves are becoming more
              frequent, and critical thresholds (like 2°C warming) will bring
              unprecedented extremes. Northern Europe is projected to get wetter
              in winters, whereas Mediterranean Europe faces drier summers and
              more droughts and fires. Sea levels along European coasts (except
              the Baltic) will rise, escalating coastal flood risks.
            </li>
            <li>
              <strong>Asia:</strong> Across Asia, average temperatures are
              rising and extreme heatwaves are intensifying (high confidence).
              Many regions will experience heavier monsoonal rainfall and
              flooding (medium to high confidence), while some areas—especially
              parts of North Asia—see longer dry seasons and expanding wildfire
              seasons. Glaciers in the Himalayas are retreating and permafrost
              is thawing, disrupting water supplies for millions (high
              confidence).
            </li>
            <li>
              <strong>Africa:</strong> Africa has been warming more rapidly than
              the world as a whole (high confidence). Heat extremes are
              increasingly common, and droughts are lengthening in several
              regions. Heavy precipitation events, however, are projected to
              intensify almost everywhere on the continent with further warming.
              Northern Africa is expected to get drier overall, worsening
              aridity, while parts of Central and East Africa may see more
              frequent intense rains and floods.
            </li>
            <li>
              <strong>Central & South America:</strong> All sub-regions in
              Central and South America are very likely to continue warming at
              above-global rates. Rainfall patterns will shift: parts of
              northwestern South America and the south-eastern cone are
              projected to get wetter, while north-eastern areas and the far
              southwest get drier. More frequent extreme downpours and pluvial
              flooding are expected in the wetter regions.
            </li>
          </ul>
        </div>
      </section>

      {/* Why It's Your Problem Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">
          Why It's <span className="italic">Your</span> Problem
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Your Footprint. Their Fire. */}
          <div className="p-6 bg-white shadow-lg rounded-lg hover:shadow-xl transition-shadow">
            <h3 className="text-xl font-extrabold text-red-600 mb-4">
              Your Footprint. Their Fire.
            </h3>
            <p className="text-gray-800">
              Every ton of carbon we emit doesn't just warm the planet in theory
              – it feeds the conditions for real disasters. Drier forests and
              hotter heatwaves are turning regions into tinderboxes. Warmer and
              drier conditions fueled by climate change have led to a sharp
              increase in wildfires in recent decades. The carbon footprint of
              industrialized lifestyles is quite literally coming back as
              firestorms and smoke, often impacting communities far from the
              source of emissions.
            </p>
          </div>

          {/* The Math of Meltdown */}
          <div className="p-6 bg-white shadow-lg rounded-lg hover:shadow-xl transition-shadow">
            <h3 className="text-xl font-extrabold text-blue-600 mb-4">
              The Math of Meltdown
            </h3>
            <p className="text-gray-800">
              It's a numbers game – and the numbers are ominous. Human activity
              has already warmed the world by ~1.1°C since the 19th century. On
              our current trajectory, we're likely to hit 1.5°C within the next
              one to two decades. Every fraction of a degree beyond that brings
              more exponential damage – from more violent storms to crop
              failures. Scientists warn that with each incremental rise in
              temperature, losses and damages escalate significantly. In simple
              terms: the more we heat, the more we harm, and the harder it gets
              to prevent total meltdown.
            </p>
          </div>

          {/* A Toxic Legacy */}
          <div className="p-6 bg-white shadow-lg rounded-lg hover:shadow-xl transition-shadow">
            <h3 className="text-xl font-extrabold text-green-700 mb-4">
              A Toxic Legacy
            </h3>
            <p className="text-gray-800">
              The climate crisis we create today is a lasting legacy that will
              outlive us. Greenhouse gases like CO<sub>2</sub> persist in the
              atmosphere for centuries – a portion will linger for tens of
              thousands of years. That means the heat and chaos we set in motion
              now will be an inheritance for our children, grandchildren, and
              many generations beyond. Already, some changes are essentially
              irreversible on human timescales – ice sheets melting, seas
              rising. We are deciding, right now, the living conditions not just
              for ourselves but for countless future lives.
            </p>
          </div>
        </div>
      </section>

      {/* Personal Stories Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          Faces of Climate Change
        </h2>
        <p className="text-gray-700 mb-6">
          Behind the statistics are real people whose lives have been altered by
          our changing climate:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {personalStories.map((person) => (
            <div
              key={person.id}
              className="bg-white p-6 rounded-xl shadow-md border-l-4 border-edu-orange"
            >
              <h3 className="font-semibold text-lg text-gray-800 mb-1">
                Meet {person.name} in {person.location}
              </h3>
              <p className="text-gray-600 mb-4">{person.story}</p>
              <blockquote className="italic text-gray-700 border-l-4 border-gray-200 pl-4">
                "{person.quote}"
              </blockquote>
            </div>
          ))}
        </div>
      </div>

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/"
        prevText="Back to Home"
        nextLink="/climate-engine"
        nextText="Next: How Earth's Climate Engine Works"
        onMarkComplete={handleMarkComplete}
        isCompleted={isCompleted}
      />
    </div>
  );
};

export default WhyItMatters;
